import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  contentChild,
  inject,
  input,
  output
} from '@angular/core';
import type { TemplateRef } from '@angular/core';
import { MatTabsModule } from '@angular/material/tabs';

import { CollapsibleComponent } from '@igo2/common/collapsible';
import {
  EntityState,
  EntityStore,
  EntityStoreFilterCustomFuncStrategy,
  EntityStoreWatcher
} from '@igo2/common/entity';
import { ListComponent, ListItemDirective } from '@igo2/common/list';
import { ConfigService } from '@igo2/core/config';
import { IgoLanguageModule } from '@igo2/core/language';

import { BehaviorSubject, EMPTY, Observable, Subscription, timer } from 'rxjs';
import { debounce, map } from 'rxjs/operators';

import { IgoMap } from '../../map/shared/map';
import { Research, SearchResult } from '../shared/search.interfaces';
import { SearchService } from '../shared/search.service';
import { SearchSource } from '../shared/sources/source';
import { TextSearchOptions } from '../shared/sources/source.interfaces';
import { SearchResultsItemComponent } from './search-results-item.component';

export enum SearchResultMode {
  Grouped = 'grouped',
  Flat = 'flat'
}

/**
 * List of search results with focus and selection capabilities.
 * This component is dumb and only emits events.
 */
@Component({
  selector: 'igo-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ListComponent,
    CollapsibleComponent,
    NgTemplateOutlet,
    SearchResultsItemComponent,
    ListItemDirective,
    MatTabsModule,
    AsyncPipe,
    IgoLanguageModule
  ]
})
export class SearchResultsComponent implements OnInit, OnDestroy {
  private cdRef = inject(ChangeDetectorRef);
  private searchService = inject(SearchService);
  private configService = inject(ConfigService);

  private showResultsCount = true;

  /**
   * Reference to the SearchResultMode enum
   * @internal
   */
  public searchResultMode = SearchResultMode;

  /**
   * Search results store watcher
   */
  private watcher: EntityStoreWatcher<SearchResult>;

  private settingsChange$$: Subscription;

  public pageIterator: { sourceId: string }[] = [];

  public collapsed: { sourceId: string }[] = [];

  readonly map = input<IgoMap>(undefined);

  /**
   * Search results store
   */
  readonly store = input<EntityStore<SearchResult>>(undefined);

  /**
   * to show hide results icons
   */
  readonly showIcons = input<boolean>(undefined);

  /**
   * Search results display mode
   */
  readonly mode = input<SearchResultMode>(SearchResultMode.Grouped);

  /**
   * Whether there should be a zoom button
   */
  readonly withZoomButton = input(false);

  /**
   * To check if the view for tabsMode for search-result-tools
   */
  readonly tabsMode = input(false);

  /**
   * Search term
   */
  @Input()
  get term(): string {
    return this._term;
  }
  set term(value: string) {
    this._term = value;
    this.pageIterator = [];
  }
  public _term: string;

  readonly settingsChange$ = input(new BehaviorSubject<boolean>(undefined));

  readonly termSplitter = input('|');

  /**
   * Event emitted when a result is focused
   */
  readonly resultFocus = output<SearchResult>();

  /**
   * Event emitted when a result is unfocused
   */
  readonly resultUnfocus = output<SearchResult>();

  /**
   * Event emitted when a result is selected
   */
  readonly resultSelect = output<SearchResult>();

  /**
   * Event emitted when a research is completed after displaying more results is clicked
   */
  readonly moreResults = output<{
    research: Research;
    results: SearchResult[];
  }>();

  /**
   * Events emitted when a result is focus or unfocus by mouse event
   */
  readonly resultMouseenter = output<SearchResult>();
  readonly resultMouseleave = output<SearchResult>();

  readonly templateSearchToolbar = contentChild<TemplateRef<any>>(
    'igoSearchItemToolbar'
  );

  get results$(): Observable<
    { source: SearchSource; results: SearchResult[] }[]
  > {
    if (this._results$ === undefined) {
      this._results$ = this.liftResults();
    }
    return this._results$;
  }
  private _results$: Observable<
    { source: SearchSource; results: SearchResult[] }[]
  >;

  /**
   * Bind the search results store to the watcher
   * @internal
   */
  ngOnInit() {
    this.watcher = new EntityStoreWatcher(this.store(), this.cdRef);

    this.settingsChange$$ = this.settingsChange$().subscribe(() => {
      this.pageIterator = [];
    });

    if (
      this.configService.getConfig('searchSources.showResultsCount') === false
    ) {
      this.showResultsCount = false;
    }
  }

  /**
   * Unbind the search results store from the watcher
   * @internal
   */
  ngOnDestroy() {
    this.watcher.destroy();
    this.settingsChange$$.unsubscribe();
  }

  /**
   * Compute a group title
   * @param group Search results group
   * @returns Group title
   * @internal
   */
  computeGroupTitle(group: {
    source: SearchSource;
    results: SearchResult[];
  }): string {
    const parts = [group.source.title];
    const count = group.results.length;
    if (count > 1 && this.showResultsCount) {
      parts.push(`(${count})`);
    }
    return parts.join(' ');
  }

  /**
   * When a result is selected, update it's state in the store and emit
   * an event. A selected result is also considered focused
   * @param result Search result
   * @internal
   */
  onResultSelect(result: SearchResult) {
    const store = this.store();
    if (store.state.get(result)) {
      if (store.state.get(result).selected === true) {
        return;
      }
    }
    store.state.update(result, { focused: true, selected: true }, true);
    this.resultSelect.emit(result);
  }

  /**
   * Return an observable of the search results, grouped by search source
   * @returns Observable of grouped search results
   * @internal
   */
  private liftResults(): Observable<
    { source: SearchSource; results: SearchResult[] }[]
  > {
    return this.store()
      .stateView.all$()
      .pipe(
        debounce((results: { entity: SearchResult; state: EntityState }[]) => {
          return results.length === 0 ? EMPTY : timer(200);
        }),
        map((results: { entity: SearchResult; state: EntityState }[]) => {
          return this.groupResults(
            results.map((r) => r.entity).sort(this.sortByOrder)
          );
        })
      );
  }

  /**
   * Sort the results by display order.
   * @param r1 First result
   * @param r2 Second result
   */
  private sortByOrder(r1: SearchResult, r2: SearchResult) {
    return r1.source.displayOrder - r2.source.displayOrder;
  }

  /**
   * Group results by search source
   * @param results Search results from all sources
   * @returns Search results grouped by source
   */
  private groupResults(
    results: SearchResult[]
  ): { source: SearchSource; results: SearchResult[] }[] {
    const grouped = new Map<SearchSource, SearchResult[]>();
    results.forEach((result: SearchResult) => {
      const source = result.source;
      let sourceResults = grouped.get(source);
      if (sourceResults === undefined) {
        sourceResults = [];
        grouped.set(source, sourceResults);
      }
      sourceResults.push(result);
    });

    return Array.from(grouped.keys()).map((source: SearchSource) => {
      if (this.pageIterator[source.getId()] === undefined) {
        this.pageIterator[source.getId()] = 1;
      }
      return { source, results: grouped.get(source) };
    });
  }

  isMoreResults(group: { source: SearchSource; results: SearchResult[] }) {
    const stategy = this.store().getStrategyOfType(
      EntityStoreFilterCustomFuncStrategy
    );
    const active = stategy?.active || false;
    if (!active) {
      return (
        group.results &&
        group.results[group.results.length - 1].meta.nextPage === true
      );
    } else {
      return (
        group.results?.length % +group.source.params.limit === 0 &&
        +group.source.params.page < 30
      );
    }
  }

  displayMoreResults(group: { source: SearchSource; results: SearchResult[] }) {
    const sourceId = group.source.getId();
    if (this.pageIterator[sourceId] === undefined) {
      this.pageIterator[sourceId] = 1;
    }
    const options: TextSearchOptions = {
      sourceId,
      page: ++this.pageIterator[sourceId]
    };

    let terms;
    const termSplitter = this.termSplitter();
    if (termSplitter && this.term.match(new RegExp(termSplitter, 'g'))) {
      terms = this.term.split(termSplitter);
    } else {
      terms = [this.term];
    }

    let researches: Research[] = [];
    terms.map((term: string) => {
      researches = researches.concat(this.searchService.search(term, options));
    });

    researches.map((research) => {
      research.request.subscribe((results: SearchResult[]) => {
        const newResults = group.results.concat(results);
        if (!results.length) {
          newResults[newResults.length - 1].meta.nextPage = false;
        }
        this.moreResults.emit({ research, results: newResults });
      });
    });
    return;
  }
}
